import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Grid,
  GridItem,
  HStack,
  Flex,
  FormErrorMessage,
  Text,
} from "@chakra-ui/react";
import ProfilePictureCropper from "../input/ProfilePictureCropper";
import { CityType, CountryType, DoctorOverview, StateType, PatientOverview } from "@/types";
import { userService } from "@/lib/services/userService";
import {
  CreatePatientUserFormData,
  createPatientUserSchema,
} from "@/lib/validations/auth";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import COUNTRIES from "@/constants/countries.json";

interface AddPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  btnRef: React.RefObject<HTMLButtonElement>;
  setPatientsData: React.Dispatch<React.SetStateAction<any[]>>;
  refreshPatients?: () => void;
  editingUser?: PatientOverview | null;
}

interface ErrorMessagesType {
  [key: string]: { message: string };
}

export default function AddPatientModal({
  isOpen,
  onClose,
  btnRef,
  setPatientsData,
  refreshPatients,
  editingUser = null,
}: AddPatientModalProps) {
  const [errorMessages, setErrorMessages] = useState<ErrorMessagesType>({});
  const [doctors, setDoctors] = useState<DoctorOverview[]>([]);
  const [states, setStates] = useState<StateType[] | null>(null);
  const [cities, setCities] = useState<CityType[] | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreatePatientUserFormData>({
    resolver: zodResolver(createPatientUserSchema as any),
  });

  // Watch the address fields
  const watchCountry = watch("address.country");
  const watchState = watch("address.state");

  // Main effect to populate form when editing
  useEffect(() => {
    if (editingUser) {
      setValue("user_firstname", editingUser.user_firstname || "");
      setValue("user_lastname", editingUser.user_lastname || "");
      setValue("user_email", editingUser.user_email || "");
      setValue("user_phone_number", editingUser.user_phone_number || "");
      
      if (editingUser.primary_doctor && editingUser.primary_doctor.id) {
        setValue("primary_doctor_id", editingUser.primary_doctor.id.toString());
      }
      
      setValue("birthday", editingUser.birthday || "");
      setValue("gender", editingUser.gender);
      setValue("blood_group", editingUser.blood_group);
      setValue("user_status", editingUser.user_status?.toString() || "1");
      
      if (editingUser.addresses?.primary) {
        const primaryAddress = editingUser.addresses.primary;
        const secondaryAddress = editingUser.addresses.secondary;
        
        setValue('address.address1', primaryAddress.street || '');
        setValue('address.address2', secondaryAddress?.street || '');
        setValue('address.pincode', primaryAddress.pincode || '');
        setValue('address.country', primaryAddress.country || '');
        setValue('address.state', primaryAddress.state || '');
        setValue('address.city', primaryAddress.city || '');

        // Set country and find states
        const selectedCountry = (COUNTRIES as CountryType[]).find(
          (country) => country.name === primaryAddress.country
        );
        
        if (selectedCountry) {
          setStates(selectedCountry.states);

          // Find and set cities based on state
          const selectedState = selectedCountry.states.find(
            (state) => state.name === primaryAddress.state
          );
          
          if (selectedState) {
            setCities(selectedState.cities);
          }
        }
      }
    } else {
      reset();
      setStates(null);
      setCities(null);
    }
  }, [editingUser, setValue, reset]);

  const onSubmit = async (data: CreatePatientUserFormData) => {
    console.log("Form Data:", data);
    let res;
    if (editingUser) {
      res = await userService.updatePatient(data, editingUser.id.toString());
    } else {
      res = await userService.createPatient(data);
    }
    
    setErrorMessages({});
    console.log("Response:", res);
    if (res.status_code == 200 || res.status_code == 201) {
      if (!editingUser) {
        setPatientsData((prev) => [...prev, res.data]);
      }
      reset();
      setStates(null);
      setCities(null);
      onClose();
      if (refreshPatients) {
        refreshPatients();
      }
    } else if (res.status_code == 409) {
      console.log("Error Response:", res);
      if (res.errors && res.errors["user_email"]) {
        setErrorMessages((prev) => ({
          ...prev,
          user_email: { message: "This email is already registered." },
        }));
      }
      if (res.errors && res.errors["user_phone_number"]) {
        setErrorMessages((prev) => ({
          ...prev,
          user_phone_number: {
            message: "This phone number is already registered.",
          },
        }));
      }
    }
  };

  const onError = (errors: any) => {
    console.log("Form Errors:", errors);
  };

  useEffect(() => {
    const fetchDoctors = async () => {
      const fetchedDoctors = await userService.getDoctors();
      if (!fetchedDoctors) return setDoctors([]);
      setDoctors(fetchedDoctors);
    };
    fetchDoctors();
  }, []);

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      finalFocusRef={btnRef}
      size={{
        base: "full",
        md: "md",
        lg: "lg",
        "2xl": "xl",
      }}
    >
      <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(1px)" />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{editingUser ? "Edit Patient" : "New Patient"}</DrawerHeader>
        <DrawerBody p={0}>
          <form onSubmit={handleSubmit(onSubmit, onError)}>
            <VStack spacing={0} align="stretch">
              {/* Patient Information Section */}
              <Heading size="sm" bg="gray.200" p={2}>
                Patient Information
              </Heading>
              <Box px={3} py={2}>
                {/* Profile Image */}
                <FormControl mb={4}>
                  <Flex align="center" gap={4}>
                    <FormLabel fontSize="sm" color="gray.600" mb={0} minW="100px">
                      Profile Image
                    </FormLabel>
                    <ProfilePictureCropper />
                  </Flex>
                </FormControl>

                {/* Name Fields */}
                <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                  <GridItem>
                    <FormControl isInvalid={!!errors.user_firstname}>
                      <FormLabel fontSize="sm">
                        First Name <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <Input
                        {...register("user_firstname")}
                        focusBorderColor="orange.500"
                      />
                      <FormErrorMessage fontSize="xs">
                        {errors.user_firstname?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isInvalid={!!errors.user_lastname}>
                      <FormLabel fontSize="sm">
                        Last Name <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <Input
                        {...register("user_lastname")}
                        focusBorderColor="orange.500"
                      />
                      <FormErrorMessage fontSize="xs">
                        {errors.user_lastname?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>

                {/* Contact Fields */}
                <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                  <GridItem>
                    <FormControl
                      isInvalid={
                        !!errors.user_phone_number ||
                        !!errorMessages.user_phone_number
                      }
                    >
                      <FormLabel fontSize="sm">
                        Phone Number <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <HStack spacing={2}>
                        <Select w="100px" fontSize="sm">
                          <option>PH +63</option>
                          <option>🇺🇸 +1</option>
                          <option>🇬🇧 +44</option>
                        </Select>
                        <Input
                          {...register("user_phone_number")}
                          type="tel"
                          placeholder="000-000-0000"
                          focusBorderColor="orange.500"
                        />
                      </HStack>
                      <FormErrorMessage fontSize="xs">
                        {errors.user_phone_number?.message ||
                          errorMessages.user_phone_number?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl
                      isInvalid={
                        !!errors.user_email || !!errorMessages.user_email
                      }
                    >
                      <FormLabel fontSize="sm">
                        Email Address <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <Input
                        {...register("user_email")}
                        type="email"
                        focusBorderColor="orange.500"
                      />
                      <FormErrorMessage fontSize="xs">
                        {errors.user_email?.message ||
                          errorMessages.user_email?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>

                {/* Doctor and DOB */}
                <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                  <GridItem>
                    <FormControl isInvalid={!!errors.primary_doctor_id}>
                      <FormLabel fontSize="sm">Primary Doctor</FormLabel>
                      <Select
                        {...register("primary_doctor_id")}
                        focusBorderColor="orange.500"
                      >
                        <option value="">Select</option>
                        {doctors?.map((doctor: DoctorOverview) => (
                          <option key={doctor.id} value={doctor.id}>
                            {doctor.user_fullname}
                            {doctor.designation_id ? ` - ${doctor.designation_id.designation}` : ''}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage fontSize="xs">
                        {errors.primary_doctor_id?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isInvalid={!!errors.birthday}>
                      <FormLabel fontSize="sm">
                        DOB <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <Input
                        {...register("birthday")}
                        type="date"
                        focusBorderColor="orange.500"
                      />
                      <FormErrorMessage fontSize="xs">
                        {errors.birthday?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>

                {/* Gender, Blood Group, Status */}
                <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl isInvalid={!!errors.gender}>
                      <FormLabel fontSize="sm">
                        Gender <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <Select
                        {...register("gender")}
                        focusBorderColor="orange.500"
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </Select>
                      <FormErrorMessage fontSize="xs">
                        {errors.gender?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isInvalid={!!errors.blood_group}>
                      <FormLabel fontSize="sm">
                        Blood Group <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <Select
                        {...register("blood_group")}
                        focusBorderColor="orange.500"
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                      </Select>
                      <FormErrorMessage fontSize="xs">
                        {errors.blood_group?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isInvalid={!!errors.user_status}>
                      <FormLabel fontSize="sm">
                        Status <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <Select
                        {...register("user_status")}
                        focusBorderColor="orange.500"
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </Select>
                      <FormErrorMessage fontSize="xs">
                        {errors.user_status?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>

              {/* Address Information Section */}
              <Heading size="sm" bg="gray.200" p={2}>
                Address Information
              </Heading>
              <Box px={3} py={2}>
                {/* Address Fields */}
                <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                  <GridItem>
                    <FormControl isInvalid={!!errors.address?.address1}>
                      <FormLabel fontSize="sm">Address 1</FormLabel>
                      <Input
                        {...register("address.address1")}
                        focusBorderColor="orange.500"
                      />
                      <FormErrorMessage fontSize="xs">
                        {errors.address?.address1?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isInvalid={!!errors.address?.address2}>
                      <FormLabel fontSize="sm">Address 2</FormLabel>
                      <Input
                        {...register("address.address2")}
                        focusBorderColor="orange.500"
                      />
                      <FormErrorMessage fontSize="xs">
                        {errors.address?.address2?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>

                {/* Location Fields */}
                <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                  <GridItem>
                    <FormControl isInvalid={!!errors.address?.country}>
                      <FormLabel fontSize="sm">Country</FormLabel>
                      <Select
                        {...register("address.country")}
                        onChange={(e) => {
                          const selectedCountry = (COUNTRIES as CountryType[]).find(
                            (country) => country.name === e.target.value
                          );
                          if (selectedCountry) {
                            setStates(selectedCountry.states);
                            setValue('address.state', '');
                            setValue('address.city', '');
                          } else {
                            setStates(null);
                          }
                          setCities(null);
                        }}
                        focusBorderColor="orange.500"
                      >
                        <option value="">Select</option>
                        {(COUNTRIES as CountryType[]).map((country: CountryType) => (
                          <option key={country.name} value={country.name}>
                            {country.name}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage fontSize="xs">
                        {errors.address?.country?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isInvalid={!!errors.address?.state}>
                      <FormLabel fontSize="sm">State</FormLabel>
                      <Select
                        {...register("address.state")}
                        onChange={(e) => {
                          const selectedState = states?.find(
                            (state) => state.name === e.target.value
                          );
                          if (selectedState) {
                            setCities(selectedState.cities);
                            setValue('address.city', '');
                          } else {
                            setCities(null);
                          }
                        }}
                        focusBorderColor="orange.500"
                      >
                        <option value="">Select</option>
                        {states &&
                          states.map((state) => (
                            <option key={state.name} value={state.name}>
                              {state.name}
                            </option>
                          ))}
                      </Select>
                      <FormErrorMessage fontSize="xs">
                        {errors.address?.state?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>

                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl isInvalid={!!errors.address?.city}>
                      <FormLabel fontSize="sm">City</FormLabel>
                      <Select
                        {...register("address.city")}
                        focusBorderColor="orange.500"
                      >
                        <option value="">Select</option>
                        {cities &&
                          cities.map((city) => (
                            <option key={city.name} value={city.name}>
                              {city.name}
                            </option>
                          ))}
                      </Select>
                      <FormErrorMessage fontSize="xs">
                        {errors.address?.city?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isInvalid={!!errors.address?.pincode}>
                      <FormLabel fontSize="sm">Pincode</FormLabel>
                      <Input
                        {...register("address.pincode")}
                        focusBorderColor="orange.500"
                      />
                      <FormErrorMessage fontSize="xs">
                        {errors.address?.pincode?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>
            </VStack>
          </form>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button bg="red.500" color="white" onClick={handleSubmit(onSubmit, onError)}>
            Save
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}